import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import {
  useCreateDisbursementForm,
  CreateDisbursementFormFields,
  BankAccountOptionRadioGroup,
  BankAccountType,
} from "./Hooks/useCreateDisbursementForm";
import OffCyclePaymentFormConfirmationDialog from "./OffCyclePaymentFormDialog/OffCyclePaymentFormConfirmationDialog";
import OffCyclePaymentFormTerminationDialog from "./OffCyclePaymentFormDialog/OffCyclePaymentFormTerminationDialog";
import useDisbursements from "../Hooks/useDisbursementsHook";
import { checkDuplicateDisbursements } from "Modules/CheckDuplicateDisbursements";
import OffCyclePaymentForm from "./OffCyclePaymentForm";
import { DPMultiStepModal } from "Components/DP";
import { DisbursementReasonCategory } from "generated/graphql";
import { useEmployeeProfileQuery } from "Hooks/useEmployeeProfileQueryContext";
import { AmplitudeProvider } from "Shared/Analytics/Amplitude";
import { AmplitudeEventType } from "Shared/Analytics/Amplitude/Types/AmplitudeEventType";

export const formatPaymentAmount = ({
  paymentAmount,
}: {
  paymentAmount: string;
}) => Math.round(parseFloat(paymentAmount) * 100);

type OffCyclePaymentFormContainerProps = {
  employeeProfileId: string;
  providerAccountId: string;
  userId: string;
};

const OffCyclePaymentFormContainer = ({
  employeeProfileId,
  providerAccountId,
  userId,
}: OffCyclePaymentFormContainerProps) => {
  const { refetchEmployeeProfile, userDetails } = useEmployeeProfileQuery();
  const defaultBankAccountValues = {
    bankAccountOptionRadioGroup:
      (userDetails!.employee.userBankAccounts?.items.length
        ? "EXISTING"
        : "NEW"
      ) as BankAccountOptionRadioGroup,
    bankAccountType: "CHECKING" as BankAccountType,
    notes: "",
    paymentAmount: "",
  };

  const methods = useForm<CreateDisbursementFormFields>({
    defaultValues: defaultBankAccountValues,
    mode: "onBlur",
  });
  const { getValues, reset, trigger: triggerValidation, watch } = methods;
  const { refetchQueries } = useDisbursements(employeeProfileId);
  const refetchUserAndDisbursements = () => {
    refetchEmployeeProfile();
    refetchQueries();
  };
  const { createDisbursement } = useCreateDisbursementForm(
    providerAccountId, // employeeProfileId,
    refetchUserAndDisbursements
  );

  const [displayDuplicateWarning, setDisplayDuplicateWarning] = useState(false);
  const [dialogStep, setDialogStep] = useState<string | undefined>();

  const isBankAccountExisting = watch("bankAccountOptionRadioGroup") === "EXISTING";
  const bankAccountOption = watch("bankAccountOption");
  const isDPASelected = isBankAccountExisting &&
    userDetails?.employee.providerBankAccount &&
    userDetails?.employee.providerBankAccount.id === bankAccountOption?.value;
  const isReasonTermination = watch("reasonOption")?.value === DisbursementReasonCategory.Termination;

  const handleConfirmationSubmit = () => {
    AmplitudeProvider.logEvent({
      type: AmplitudeEventType.CyclePaymentSubmitClicked,
      payload: { knownTrackingId: userDetails?.employee.knownTrackingId || "" },
    });

    const values = getValues();
    createDisbursement(values);
    reset({
      bankAccountOption: null,
      reasonOption: null,
      ...defaultBankAccountValues,
    });

    setDialogStep(
      isReasonTermination && !userDetails!.partnerCanceledAt && !isDPASelected
        ? "TERMINATION"
        : undefined
    );
  };

  const handleCancelDialog = () => setDialogStep(undefined);

  const dialogSteps = {
    CONFIRMATION: (
      <OffCyclePaymentFormConfirmationDialog
        displayDuplicateWarning={displayDuplicateWarning}
        displayTerminationWarning={!!isDPASelected && isReasonTermination}
        onCancel={handleCancelDialog}
        onSubmit={handleConfirmationSubmit}
      />
    ),
    TERMINATION: (
      <OffCyclePaymentFormTerminationDialog onClose={handleCancelDialog} />
    ),
  };

  const { data } = useDisbursements(employeeProfileId);

  const advanceDialogToConfirmationStep = () => {
    const disbursements = data && data.employeeProfile.disbursements.items;
    const paymentAmount = formatPaymentAmount(getValues());
    const duplicateFound = checkDuplicateDisbursements(
      disbursements,
      paymentAmount
    );

    setDisplayDuplicateWarning(duplicateFound);
    setDialogStep("CONFIRMATION");
  };

  const handleClickSubmit = async () => {
    const isValid = await triggerValidation();
    if (isValid) {
      advanceDialogToConfirmationStep();
    }
  };

  return (
    <FormProvider {...methods}>
      <form>
        <OffCyclePaymentForm
          onSubmit={handleClickSubmit}
          employeeProfileId={employeeProfileId}
          userId={userId}
        />
        <DPMultiStepModal
          currentStep={dialogStep}
          setCurrentStep={setDialogStep}
          steps={dialogSteps}
        />
      </form>
    </FormProvider>
  );
};

export default OffCyclePaymentFormContainer;
